import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { SiteWidth } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { h3, paragraphLarge, paragraphRegular, paragraphSmall } from '@helpers/typography';
import svgTick from '@img/icons/background/tick.svg?url';
import { CommonButtonStyles } from '@stories/Components/Global/Buttons/Button/Button.styles';
import { CommonErrorStyles, CommonLabelStyles } from '@stories/Components/Global/Forms/Form.styles';
import { CommonCheckboxContainerStyles, CommonCheckboxLabelStyles, } from '@stories/Components/Global/Inputs/CheckboxList/CheckboxList.styles';
import { CommonRadioListContainerStyles, CommonRadioListLabelStyles, } from '@stories/Components/Global/Inputs/RadioList/RadioList.styles';
import { CommonDropdownWrapperStyles, CommonSelectStyles, } from '@stories/Components/Global/Inputs/Select/Select.styles';
import { CommonTextareaStyles } from '@stories/Components/Global/Inputs/Textarea/Textarea.styles';
import { CommonTextInputStyles } from '@stories/Components/Global/Inputs/Textbox/Textbox.styles';
import { modalBackgroundStyles } from '@stories/Components/Global/Modal/Background/Background.styles';
import { modalCloseButtonStyles } from '@stories/Components/Global/Modal/Close/Close.styles';
import { modalContainerStyles, modalContentContainerStyles, modalContentInnerStyles, modalInnerStyles, } from '@stories/Components/Global/Modal/Modal.styles';
import { mustReadModalButtonWrapperStyles, mustReadModalContainerStyles, mustReadModalContentStyles, mustReadModalIndicatorStyles, mustReadModalInnerStyles, mustReadModalTitleStyles, } from '@stories/Components/MustReadModal/MustReadModal.styles';
import React from 'react';
import { createGlobalStyle } from 'styled-components';
export const KenticoFormStyles = createGlobalStyle `
  .kentico-form-widget {
    --borderColour: ${brand.ess.secondary.midGrey};

    [data-site='ess'] & {
      --textColor: ${brand.ess.primary};
    }

    [data-site='mep'] & {
      --textColor: ${brand.mep.primary};
    }

    margin: var(--sectionSpacing) auto;
    overflow: visible;
    background-color: ${brand.global.white};
    padding: 0 var(--sitePadding);
    max-width: ${SiteWidth * 0.66}px;
    
    &.kentico-form-widget--no-bg {
      background: none;
    }

    &__title {
      ${h3};

      > span {
        color: ${brand.global.red};
      }
    }

    &__content {
      ${paragraphRegular};
      
      max-width: 580px;
      margin: 0 0 ${fluid(30, 42)};

      &.kentico-form-widget__content--complete {
        ${paragraphLarge};
      }
    }
  }

  .kentico-form {
    form {
      display: flex;
      flex-direction: column;

      @media ${from(Device.MobileLarge)} {
        align-items: flex-end;
      }
    }

    .kentico-modal-button,
    input[type='submit'] {
        ${transition('background-size, border-color, color, text-decoration-color', 0.2)};

      ${ButtonReset};
      ${fonts.daxWide.bold};
      ${CommonButtonStyles};

      font-size: ${fluid(16, 18)};
      line-height: 1em;
      text-decoration: none;
      text-transform: uppercase;
      text-align: center;
      height: ${fluid(44, 46)};
      padding: 0 ${fluid(12, 16)};

      /* Colour settings */
      [data-site='ess'] & {
        --borderWidth: 1px;
        --colourBorder: ${brand.global.red};
        --colourBorderActive: ${brand.ess.secondary.darkGrey};
        --colourBg: ${brand.global.red};
        --colourBgActive: ${brand.ess.secondary.darkGrey};
        --colourText: ${brand.global.white};
        --colourTextActive: ${brand.global.white};
        --disabledButtonColour: ${brand.ess.secondary.midGrey};
      }

      [data-site='mep'] & {
        --borderWidth: 1px;
        --colourBorder: ${brand.global.red};
        --colourBorderActive: ${brand.global.white};
        --colourBg: ${brand.global.red};
        --colourBgActive: ${brand.global.white};
        --colourText: ${brand.global.white};
        --colourTextActive: ${brand.global.red};
        --disabledButtonColour: ${brand.mep.secondary.midGrey};
        --hoverOpacity: 0.15;

        border-radius: 6px;
      }

      :focus-visible & {
        outline: auto;
        outline-offset: 3px;
        outline-width: 2px;
        outline-style: solid;
      }

      &:focus-visible,
      &:hover {
        color: var(--colourTextActive);
        border-color: var(--colourBorderActive);
        background-size: 100% 100%;
        text-decoration-color: var(--colourTextDecorationActive);
      }

      &[disabled] {
        border-color: var(--disabledButtonColour);
        background-color: var(--disabledButtonColour);
        pointer-events: none;
      }

      /* Mobile click styles - button should mimic press */
      @media ${until(Device.TabletLarge)} {
        ${transition('background-size, border-color, color, text-decoration-color, transform', 0.15)};

        &:active {
          transform: translateY(1.5px);
        }
      }
    }

    .ktc-default-section {
      display: grid;
      gap: ${fluid(20, 24)};
      grid-template-columns: 1fr;
      justify-items: flex-start;
      margin: 0 0 ${fluid(32, 40)};
      width: 100%;

      @media ${from(Device.TabletLarge)} {
        &--two-column {
          grid-template-columns: 1fr 1fr;

          .form-field--wide {
            grid-column: 1 / span 2;
          }
        }
      }

      hr {
        display: block;
        border: 1px solid var(--borderColour);
        border-bottom: 0;
        grid-column: 1 / span 1;
        height: 0;
        margin: ${fluid(32, 40)} 0;
        width: 100%;

        @media ${from(Device.MobileLarge)} {
          grid-column: 1 / span 2;
        }
      }
    }

    .form-field {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .control-label {
      ${CommonLabelStyles};
    }

    .editing-form-control-nested-control {
      display: block;
    }

    .field-validation-valid,
    .field-validation-error,
    .validation-summary-errors  {
      ${CommonErrorStyles};
    }

    .explanation-text {
      ${paragraphSmall};

      color: ${brand.ess.secondary.darkGrey}
    }

    input.form-control:not([type="file"]) {
      ${CommonTextInputStyles};
    }

    .ktc-select .editing-form-control-nested-control {
      ${CommonDropdownWrapperStyles};

      &::after {        
        transform: translateY(calc(-50% - 4px));
      }
    }

    select.form-control {
      ${CommonSelectStyles};
    }

    textarea.form-control {
      ${CommonTextareaStyles};
    }

    .ktc-file-component-wrapper {
      display: block;

      label {
        display: block;
      }

      .form-control {
        display: block;
      }

      .ktc-uploader-replacement {
        display: block;
      }

      .ktc-uploader-text {
        ${paragraphSmall};
        
        display: block;
      }

      .ktc-uploader-icon-button {
        display: none;
      }
    }

    .ktc-checkbox-list {
      ${CommonCheckboxContainerStyles};
    }

    .ktc-radio-list {
      ${CommonRadioListContainerStyles};
    }

    .ktc-checkbox {
      .form-control {
        ${srOnly}
      }

      label {
        ${CommonCheckboxLabelStyles};
      }
      
      .form-control:checked + label::before {
        background-color: ${brand.global.white};
        background-image: url(${svgTick});
      }
    }

    .ktc-radio {
      .form-control {
        ${srOnly}
      }

      label {
        ${CommonRadioListLabelStyles}; 
      }
      
      .form-control:checked + label::before {
        background-color: ${brand.global.red};
      }
    }
  }

  .form-alert--container {
    position: relative;
    display: block;
    overflow: hidden;
    user-select: none;

    .form-alert--inner {
      ${transition('background-color, color')};
      
      display: block;
      background: ${brand.global.red};
      color: ${brand.global.white};
      padding: 16px 60px 16px 24px;
    }

    .form-alert--content{
      ${paragraphSmall};

      display: block;
      margin: 0;

      a {
        color: currentColor;
      }

      h3 {
        ${h3};
      }
    }
  }

  /**
  * Kentico Modal Styles
  *
  * Uses the Modal and MustReadModal components styles to create a modal for the Kentico form
  */  
  .kentico-modal {    
    ${modalContainerStyles};

    --fgColor: var(--textColor);

    .kentico-modal-background {
      ${modalBackgroundStyles};
    }

    .kentico-modal-close {
      ${modalCloseButtonStyles};
    }

    .kentico-modal-inner {
      ${modalInnerStyles};
    }

    .kentico-modal-content {
      ${modalContentContainerStyles};
      ${mustReadModalContainerStyles};
    }

    .kentico-modal-content-inner {
      ${modalContentInnerStyles};
      ${mustReadModalInnerStyles};
    }

    .kentico-modal-title {
      ${mustReadModalTitleStyles};
    }

    .kentico-modal-terms {
      ${mustReadModalContentStyles};
    }

    .kentico-modal-indicator {
      ${mustReadModalIndicatorStyles};
    }

    .kentico-modal-button-wrapper {
      ${mustReadModalButtonWrapperStyles};
    }
  }
`;
const KenticoForm = () => {
    // Add listeners to control the modal
    React.useEffect(() => {
        // select the target node
        const target = document.querySelector('.kentico-form-widget');
        const mutationTarget = document.querySelector('.kentico-form');
        if (target) {
            // create an observer instance
            const observer = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    if (mutation.type === 'childList' && mutation.target === mutationTarget) {
                        configureModalListeners(observer);
                    }
                });
            });
            // configuration of the observer:
            const config = {
                attributes: true,
                characterData: true,
                childList: true,
                subtree: true,
                attributeOldValue: true,
                characterDataOldValue: true,
            };
            // pass in the target node, as well as the observer options
            observer.observe(target, config);
        }
        configureModalListeners();
    }, []);
    function configureModalListeners(observer) {
        const modal = document.querySelector('.kentico-modal');
        if (!modal) {
            return () => false;
        }
        const formSumitButton = document.querySelector('.kentico-form input[type=submit]');
        const modalOpenButton = document.querySelector('.modal-open-button');
        const modalCloseButton = modal.querySelector('.kentico-modal-close');
        const modalBackground = modal.querySelector('.kentico-modal-background');
        const modalSubmit = modal.querySelector('.modal-submit-button');
        const modalCheckbox = modal.querySelector('input[type=checkbox]');
        const modalTermsText = modal.querySelector('.kentico-modal-terms');
        /**
         * If observer is passed and modalCheckbox is already checked, return false and disconnect the observer.
         * The modal terms have been accepted at this point so the modal can be disabled.
         */
        if (observer && modalCheckbox.checked) {
            observer.disconnect();
            return () => false;
        }
        function openModal(e) {
            e.preventDefault();
            modal?.setAttribute('data-open', 'true');
        }
        function closeModal(e) {
            e.preventDefault();
            modal?.setAttribute('data-open', 'false');
        }
        function handleScroll() {
            if (!modalTermsText) {
                return;
            }
            const indicator = modalTermsText.querySelector('.kentico-modal-indicator');
            // Reduce scrollHeight by 1px to compensate for pixel rounding
            if (modalTermsText.scrollTop + modalTermsText.offsetHeight >=
                modalTermsText.scrollHeight - 1) {
                if (indicator) {
                    indicator.style.opacity = '0';
                }
                modalTermsText.setAttribute('data-scrolled', 'true');
                return modalSubmit?.removeAttribute('disabled');
            }
            if (indicator) {
                indicator.style.opacity = '1';
            }
            modalTermsText.setAttribute('data-scrolled', 'false');
            return modalSubmit?.setAttribute('disabled', 'true');
        }
        function submitModal(e) {
            e.preventDefault();
            if (modalCheckbox) {
                if (modalCheckbox.checked === false &&
                    modalCheckbox.getAttribute('data-val-required')) {
                    const error = modalCheckbox.getAttribute('data-val-required');
                    const validationMsg = modalCheckbox.parentElement?.nextElementSibling;
                    if (validationMsg && error) {
                        validationMsg.classList?.add('field-validation-error');
                        validationMsg.innerText = error;
                        return;
                    }
                }
            }
            return formSumitButton?.click();
        }
        modalOpenButton?.addEventListener('click', (e) => openModal(e));
        modalBackground?.addEventListener('click', (e) => closeModal(e));
        modalCloseButton?.addEventListener('click', (e) => closeModal(e));
        modalSubmit?.addEventListener('click', (e) => submitModal(e));
        modalTermsText?.addEventListener('scroll', () => handleScroll());
        return () => {
            modalOpenButton?.removeEventListener('click', (e) => openModal(e));
            modalBackground?.removeEventListener('click', (e) => closeModal(e));
            modalCloseButton?.removeEventListener('click', (e) => closeModal(e));
            modalSubmit?.removeEventListener('click', (e) => submitModal(e));
            modalTermsText?.removeEventListener('scroll', () => handleScroll());
        };
    }
    return React.createElement(KenticoFormStyles, null);
};
export default KenticoForm;
